@if (state.creatingReviewItemError) {
  <div class="absolute left-0 top-0 z-[1000] flex h-full w-full flex-col items-center justify-center bg-white">
    @if (state.creatingReviewItemError === CreatingReviewItemBackendError.NoCandidateQuestions) {
      <div>No reference questions left for this mode.</div>
    } @else if (state.creatingReviewItemError === CreatingReviewItemBackendError.UserHasNoPermission) {
      <div class="text-red-500">You do not have permission to review this item</div>
    } @else {
      <div class="text-red-500">{{ state.creatingReviewItemError }}</div>
    }
  </div>
} @else if (!state.mode) {
  <div class="absolute left-0 top-0 z-[1000] flex h-full w-full flex-col items-center justify-center bg-white">
    <div>Invalid review mode</div>
  </div>
} @else if (!state.refQuestion || state.creatingReviewItem) {
  <div class="absolute left-0 top-0 z-[1000] flex h-full w-full flex-col items-center justify-center bg-white">
    <dojo-loader [centered]="true" />
  </div>
}

<as-split [direction]="'horizontal'" unit="pixel" class="h-full" [gutterSize]="1" [useTransition]="true">
  <as-split-area [size]="500" [minSize]="200" class="flex h-full flex-col" [visible]="showPdfViewer()">
    <dojo-question-validation-header>
      <div class="flex w-full items-center justify-between">
        <span class="whitespace-nowrap">Original PDFs</span>

        <mat-button-toggle-group
          class="custom-toggle-group size-s"
          hideSingleSelectionIndicator
          [value]="sourcePreviewPlace()"
          (change)="sourcePreviewPlace.set($event.value)"
        >
          <mat-button-toggle [value]="'questions'">Question</mat-button-toggle>
          <mat-button-toggle [value]="'markschemes'">Markscheme</mat-button-toggle>
          <mat-button-toggle [value]="'raw-markscheme'">Raw MS</mat-button-toggle>
        </mat-button-toggle-group>
      </div>
    </dojo-question-validation-header>

    <div class="h-full overflow-y-auto overflow-x-hidden">
      @if (sourcePreviewPlace(); as sourcePreviewPlace) {
        @if (sourcePreviewPlace === 'raw-markscheme') {
          <div class="flex h-full flex-col items-center justify-center">
            <div class="scrollbar-gutter-stable h-full w-full overflow-y-auto border-b-2 p-4">
              @if (state.rawMarkscheme) {
                <dojo-markdown-viewer [content]="state.rawMarkscheme" />
              } @else {
                Missing raw markscheme
              }
            </div>
            <div class="scrollbar-gutter-stable h-full w-full overflow-y-auto p-4">
              {{ state.rawMarkscheme }}
            </div>
          </div>
        } @else {
          @if (state.pdfUrls) {
            @for (pdfUrl of state.pdfUrls[sourcePreviewPlace]; track pdfUrl.path) {
              <dojo-pdf-viewer
                #pdfViewer
                [src]="pdfUrl.signedUrl"
                [name]="pdfUrl.path ?? ''"
                [page]="state.pdfNumber"
              />
            }
          } @else {
            <div class="flex h-full flex-col items-center justify-center">
              <dojo-loader />
            </div>
          }
        }
      }
    </div>
  </as-split-area>
  <as-split-area class="flex h-full flex-col !overflow-hidden" [size]="'*'" [minSize]="200">
    <div class="z-3 sticky top-0">
      <dojo-question-validation-header>To review & fix</dojo-question-validation-header>

      <div class="bg-neutral-0 flex items-center gap-1 border-b border-neutral-200 py-1.5 pl-3 pr-4">
        <dojo-icon-button
          [icon]="showPdfViewer() ? 'file-slash' : 'file-pdf'"
          (click)="showPdfViewer.set(!showPdfViewer())"
          [matTooltip]="showPdfViewer() ? 'Close PDF Viewer' : 'Open PDF Viewer'"
        />
        <dojo-icon-button
          [icon]="'crosshairs'"
          (click)="scrollToReferenceQuestion()"
          [matTooltip]="'Scroll to position in the PDF'"
          [disabled]="state.pdfNumber === undefined"
        />
        @if (state.refQuestion) {
          <div class="text-xs text-neutral-400">
            Question #<span class="font-bold">{{ state.refQuestion.question_number }}</span> (ID:
            {{ state.refQuestion.id }})
          </div>
        }
        <div class="ml-auto flex items-center gap-1">
          @if (state.anyEditingAllowed) {
            <dojo-button
              [size]="'xs'"
              [fill]="'outline'"
              [customColor]="'sky'"
              [disabled]="!state.canUndo"
              (click)="undo()"
              [matTooltip]="'Undo'"
            >
              <dojo-icon icon="undo" />
            </dojo-button>
            <dojo-button
              [size]="'xs'"
              [fill]="'outline'"
              [customColor]="'sky'"
              [disabled]="!state.canRedo"
              (click)="redo()"
              [matTooltip]="'Redo'"
            >
              <dojo-icon icon="redo" />
            </dojo-button>
          }
          @if (state.isAdmin || state.config?.allowJsonEditor) {
            <dojo-button [size]="'xs'" [fill]="'outline'" [customColor]="'sky'" dojoJsonEditorTrigger>
              JSON
            </dojo-button>
          }
        </div>

        @if (state.config?.allowQuestionStructureEditing) {
          <dojo-new-question-item-button class="ml-2" />
        }
      </div>
    </div>
    <div class="scrollbar-gutter-stable overflow-y-auto pl-2">
      <dojo-question-items-editor
        [calculatorAllowed]="state.calculatorAllowed"
        [markCalculationMethod]="state.markCalculationMethod"
        [allowCalculatorEdit]="state.isCalculatorEditingAllowed"
        (calculatorAllowedChange)="onCalculatorAllowedChange($event)"
      />
    </div>
  </as-split-area>

  <!-- Sidebar -->
  <as-split-area class="flex h-full flex-col !overflow-y-hidden" [minSize]="280" [maxSize]="600" [size]="400">
    @if (state.refQuestion && state.mode) {
      <dojo-reference-question-sidebar
        [hidden]="state.activeItem || state.activeSubItem"
        [mode]="state.mode"
        [refQuestion]="state.refQuestion"
        [isSubmitting]="state.isSendingFeedback"
        [calculatorAllowed]="state.calculatorAllowed"
        (formStateChange)="formState.set($event)"
        class="flex h-full flex-col"
      />
      @if (state.activeItem || state.activeSubItem) {
        <dojo-question-active-item-editor />
      }
    } @else {
      <div class="flex h-full flex-col items-center justify-center">
        <dojo-loader />
      </div>
    }
  </as-split-area>
</as-split>

<!-- Footer -->
<div class="sticky bottom-0 flex justify-between gap-2 border-t border-neutral-200 bg-neutral-50 p-2 px-4">
  @if (state.config?.isSkippable) {
    <dojo-button
      [disabled]="!state.refQuestion || state.isSaving"
      [pending]="isSkipping()"
      [fill]="'outline'"
      (click)="openSkipQuestionDialog()"
    >
      Skip question
    </dojo-button>
  } @else {
    <!-- Empty space to push the next button to the right -->
    <div></div>
  }
  <div class="flex items-center gap-2">
    @if (state.config?.canStoreInBraintrust && state.isAdmin) {
      <mat-slide-toggle
        class="small mr-2"
        color="primary"
        [checked]="storeInBraintrust()"
        (change)="storeInBraintrust.set(!storeInBraintrust())"
      >
        Store in Braintrust
      </mat-slide-toggle>
      @if (storeInBraintrust()) {
        <input
          class="text-md border-neutral-200 bg-white p-[5px]"
          style="border-radius: 0.5rem"
          type="text"
          [value]="brainTrustDataset()"
          (input)="onBrainTrustDatasetChange($event)"
        />
      }
    }
    @if (state.formState) {
      <div
        [matTooltip]="
          !state.isQuestionItemsFormValid || !state.formState.canSubmit
            ? !state.isQuestionItemsFormValid
              ? 'Please fix the errors in the question editor. Solution paths in the markscheme may need to be regenerated.'
              : 'Please finish evaluating the question'
            : ''
        "
        [matTooltipPosition]="'above'"
      >
        <dojo-button
          [customColor]="
            state.formState.canSubmit && state.formState.submitText === SubmitButtonText.ApproveContinue
              ? 'green'
              : state.formState.canSubmit
                ? 'red'
                : undefined
          "
          (click)="saveAndContinue()"
          [disabled]="!state.formState.canSubmit || !state.isQuestionItemsFormValid"
          [pending]="state.isSaving || state.isSendingFeedback"
        >
          {{ state.formState.submitText }}
        </dojo-button>
      </div>
    }
  </div>
</div>
